import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ConfiguratorDatastoreService, DatastoreDto } from '@tj/tj-dmp-lib-api';
import { firstValueFrom } from 'rxjs';
import { KeycloakService } from 'keycloak-angular';
import { AccountService } from '../../auth/account.service';

interface SupersetGuestTokenResponse {
    guestToken: string;
    supersetUrl: string;
    dashboardEmbedId: string;
}

// TODO: remove once per-tenant/affiliate dashboards are configurable via an admin screen.
// Today only this tenant has a Superset dashboard configured server-side.
const CONFIGURED_TENANT = 'T_AV';

@Component({
    selector: 'dmp-superset-dashboard',
    templateUrl: './superset-dashboard.component.html',
    styleUrls: ['./superset-dashboard.component.scss'],
    standalone: false
})
export class SupersetDashboardComponent implements OnInit {
    @ViewChild('embedContainer', { static: true }) embedContainer: ElementRef<HTMLDivElement>;

    showTenantSelector = false;
    loading = false;
    error = false;
    notConfigured = false;
    private currentDatastore: string = null;

    constructor(
        private readonly http: HttpClient,
        private readonly keycloak: KeycloakService,
        private readonly accountService: AccountService,
        private readonly datastoreService: ConfiguratorDatastoreService,
    ) {
    }

    ngOnInit(): void {
        if (this.accountService.isSuperAdmin()) {
            this.showTenantSelector = true;
            return;
        }
        this.datastoreService.getAllDatastoresUsingGET().subscribe(datastores => {
            const ownDatastore = datastores?.[0];
            if (ownDatastore) {
                this.onDatastoreChange(ownDatastore);
            } else {
                this.notConfigured = true;
            }
        });
    }

    onDatastoreChange(datastore: DatastoreDto) {
        this.currentDatastore = datastore ? datastore.code : null;
        this.embedContainer.nativeElement.innerHTML = '';
        this.error = false;
        this.notConfigured = false;
        if (this.currentDatastore === CONFIGURED_TENANT) {
            this.loadDashboard();
        } else if (this.currentDatastore) {
            this.notConfigured = true;
        }
    }

    private async loadDashboard() {
        this.loading = true;
        this.error = false;
        try {
            const { embedDashboard } = await import('@superset-ui/embedded-sdk');
            const response = await this.fetchGuestToken();
            await embedDashboard({
                id: response.dashboardEmbedId,
                supersetDomain: response.supersetUrl,
                mountPoint: this.embedContainer.nativeElement,
                fetchGuestToken: () => this.fetchGuestToken().then(r => r.guestToken),
                dashboardUiConfig: { hideTitle: true },
            });
        } catch (err) {
            console.error('Superset dashboard load failed', err);
            this.error = true;
        } finally {
            this.loading = false;
        }
    }

    private async fetchGuestToken(): Promise<SupersetGuestTokenResponse> {
        const token = await this.keycloak.getToken();
        return firstValueFrom(this.http.get<SupersetGuestTokenResponse>(
            '/services/configurator/api/superset/guest-token',
            { headers: { 'X-DataStoreID': this.currentDatastore, Authorization: 'Bearer ' + token } }
        ));
    }
}
